/**
 * Personal code from an outreach link — `?p=<code>` (KAN-1574).
 *
 * The platform's sales CRM gives every company card a random code; the
 * outreach e-mail links to a shop or to the signup with it. Then:
 *
 * - on a **shop** every page load reports it once, which binds the
 *   visitor's anonymous shop session to the card
 *   (`POST /public/personal-code/visit`);
 * - the **signup** form sends it along, which pairs the new shop with the
 *   card.
 *
 * Pairing a visit with an outreach is marketing, so it needs the visitor's
 * marketing consent (KAN-1965). Without it the code is held in page memory
 * only: nothing is stored in the browser and no visit is reported. A later
 * consent from the cookie banner stores the code and reports the visit
 * (`applyMarketingConsent`); a withdrawn consent deletes the stored code.
 * The one use without consent is the signup form opened straight from the
 * link: the code is in the address and goes along with the form.
 *
 * No pixel, no e-mail tracking: only our own pages see the code. The code
 * carries nothing personal. The shape mirrors the backend
 * (`services/crm.normalize_personal_code`); anything else is ignored.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { apiFetch } from "@/lib/api";
import { getConsentCategory } from "@/lib/consent";

export const PERSONAL_CODE_PARAM = "p";
const STORAGE_KEY = "forgmatic.personal-code";
/** A code older than this belongs to a campaign that is over. */
const MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[abcdefghjkmnpqrstuvwxyz23456789]{10}$/;

export function normalizePersonalCode(
  raw: string | null | undefined,
): string | null {
  const value = (raw ?? "").trim().toLowerCase();
  return CODE_RE.test(value) ? value : null;
}

export function rememberPersonalCode(
  code: string,
  now: number = Date.now(),
): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, at: now }));
  } catch {
    // Private mode / blocked storage — the code still works on this page.
  }
}

export function storedPersonalCode(now: number = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown; at?: unknown };
    if (typeof parsed.at !== "number" || now - parsed.at > MAX_AGE_MS)
      return null;
    return normalizePersonalCode(
      typeof parsed.code === "string" ? parsed.code : null,
    );
  } catch {
    return null;
  }
}

function forgetPersonalCode(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage holds nothing to forget.
  }
}

/** The code from the address of this page load, kept in memory only. */
let pageCode: string | null = null;
// Once per page load: a visit, not a page view. The backend merges requests
// of one session that are close together anyway.
let visitReported = false;
/** Set by the hook: may this page report a visit (a shop, not admin)? */
let visitAllowed = false;

/**
 * The code to use on this page. With marketing consent: the code in the
 * address (remembered as a side effect), else the stored one. Without it:
 * only the code in the address, and nothing is stored.
 */
export function capturePersonalCode(search: string): string | null {
  const fromUrl = normalizePersonalCode(
    new URLSearchParams(search).get(PERSONAL_CODE_PARAM),
  );
  if (fromUrl) pageCode = fromUrl;
  if (!getConsentCategory("marketing")) {
    // Also clears a code stored before consent was required.
    forgetPersonalCode();
    return fromUrl;
  }
  if (fromUrl) {
    rememberPersonalCode(fromUrl);
    return fromUrl;
  }
  return storedPersonalCode() ?? pageCode;
}

function reportVisitOnce(code: string | null): void {
  if (!code || !visitAllowed || visitReported) return;
  if (!getConsentCategory("marketing")) return;
  visitReported = true;
  apiFetch<void>("/api/v1/public/personal-code/visit", {
    method: "POST",
    body: { code },
  }).catch(() => {
    // Measuring a visit must never disturb the visitor.
  });
}

/**
 * Capture `?p=` on a page and, on a shop with marketing consent, report the
 * visit. The hook below calls it on every navigation.
 */
export function handlePersonalCode(search: string, canReport: boolean): void {
  visitAllowed = canReport;
  reportVisitOnce(capturePersonalCode(search));
}

/**
 * Apply a marketing consent decision from the cookie banner right away,
 * like `applyAnalyticsConsent` does for Sentry: a granted consent stores
 * the code this page came with and reports the visit, a withdrawn one
 * deletes the stored code.
 */
export function applyMarketingConsent(granted: boolean): void {
  if (!granted) {
    forgetPersonalCode();
    return;
  }
  if (pageCode) rememberPersonalCode(pageCode);
  reportVisitOnce(storedPersonalCode() ?? pageCode);
}

/**
 * Capture `?p=` on any page; on a shop also report the visit. Mounted once
 * at the root so a link to any page works, not just to the home page.
 */
export function usePersonalCodeCapture({
  reportVisit,
}: {
  reportVisit: boolean;
}): void {
  const { search, pathname } = useLocation();
  const isAdmin = /\/[a-z]{2}\/(admin|shopfloor)(\b|\/)/.test(pathname);
  useEffect(() => {
    handlePersonalCode(search, reportVisit && !isAdmin);
  }, [search, reportVisit, isAdmin]);
}
